import { useState, useEffect, useCallback } from "react";

// Utility to get element dimensions with fallbacks
const getElementDimensions = (element: HTMLElement | null) => {
  if (!element) {
    return { width: 0, height: 0 };
  }
  
  const boundingRect = element.getBoundingClientRect();
  const computedStyle = window.getComputedStyle(element);

  // Fallbacks to handle different browser behaviors
  const width = boundingRect.width || parseFloat(computedStyle.width) || element.clientWidth;
  const height = boundingRect.height || parseFloat(computedStyle.height) || element.clientHeight;

  return { width, height };
};

export const useResize = (ref: React.RefObject<HTMLElement>) => {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const updateDimensions = useCallback(() => {
    if (ref.current) {
      const { width, height } = getElementDimensions(ref.current);
      setDimensions({ width, height });
    }
  }, [ref]);

  useEffect(() => {
    updateDimensions();

    // Add event listeners for resize and possibly orientation change
    window.addEventListener("resize", updateDimensions);
    window.addEventListener("orientationchange", updateDimensions);

    return () => {
      window.removeEventListener("resize", updateDimensions);
      window.removeEventListener("orientationchange", updateDimensions);
    };
  }, [updateDimensions]);

  return dimensions;
};
